import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiDollarSign, FiShoppingBag, FiPackage, FiStar, FiUsers, FiTrendingUp,
  FiTrendingDown, FiAlertTriangle, FiPlus, FiBarChart2, FiPieChart, FiClock,
  FiRefreshCw, FiBox, FiCheckCircle, FiXCircle,
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import merchantAnalyticsService from '../../services/merchant-analytics.service';

const fmt = (n) => `$${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtShort = (n) => {
  const v = parseFloat(n || 0);
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

const COLORS = ['#B91C1C', '#EAB308', '#059669', '#2563EB', '#9333EA', '#EA580C', '#0891B2', '#15803D'];
const STATUS_COLORS = {
  pending: '#F59E0B', confirmed: '#3B82F6', processing: '#6366F1',
  shipped: '#8B5CF6', delivered: '#10B981', cancelled: '#EF4444', refunded: '#6B7280',
};

const KpiCard = ({ icon: Icon, label, value, growth, prefix = '', color = '#1F2937' }) => (
  <div className="dashboard-card p-3">
    <div className="d-flex justify-content-between align-items-start mb-2">
      <div style={{
        width: '40px', height: '40px', borderRadius: '10px',
        background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} color={color} />
      </div>
      {growth !== undefined && growth !== null && (
        <span style={{
          fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '2px',
          color: growth >= 0 ? '#059669' : '#DC2626',
        }}>
          {growth >= 0 ? <FiTrendingUp size={12} /> : <FiTrendingDown size={12} />}
          {Math.abs(growth)}%
        </span>
      )}
    </div>
    <div style={{ fontSize: '22px', fontWeight: 800, color }}>{prefix}{value}</div>
    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{label}</div>
  </div>
);

const SectionTitle = ({ icon: Icon, title, action }) => (
  <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
    <h5 style={{ fontWeight: 700, fontSize: '16px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Icon size={18} color="#B91C1C" /> {title}
    </h5>
    {action}
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1F2937', color: '#FFF', padding: '10px 14px', borderRadius: '10px',
      fontSize: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: '4px' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>
          {p.name}: {p.name.toLowerCase().includes('revenue') ? fmt(p.value) : p.value}
        </div>
      ))}
    </div>
  );
};

const MerchantDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [orders, setOrders] = useState(null);
  const [products, setProducts] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [revDays, setRevDays] = useState(30);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ovRes, revRes, ordRes, prodRes, custRes, revwRes] = await Promise.allSettled([
        merchantAnalyticsService.getOverview(),
        merchantAnalyticsService.getRevenue(revDays),
        merchantAnalyticsService.getOrders(),
        merchantAnalyticsService.getProducts(),
        merchantAnalyticsService.getCustomers(),
        merchantAnalyticsService.getReviews(),
      ]);
      if (ovRes.status === 'fulfilled') setOverview(ovRes.value.data.data);
      if (revRes.status === 'fulfilled') setRevenue(revRes.value.data.data);
      if (ordRes.status === 'fulfilled') setOrders(ordRes.value.data.data);
      if (prodRes.status === 'fulfilled') setProducts(prodRes.value.data.data);
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data.data);
      if (revwRes.status === 'fulfilled') setReviews(revwRes.value.data.data);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (!loading) {
      merchantAnalyticsService.getRevenue(revDays).then(r => setRevenue(r.data.data)).catch(() => {});
    }
  }, [revDays]);

  if (loading) return <LoadingSpinner />;

  const ov = overview;
  const funnel = orders?.funnel;

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h3 style={{ fontWeight: 800, margin: 0 }}>Dashboard</h3>
          <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
            Welcome back, {user?.firstName}! Here's your store performance.
          </p>
        </div>
        <div className="d-flex gap-2 mt-2 mt-md-0">
          <button onClick={fetchAll} className="btn btn-sm btn-outline-secondary">
            <FiRefreshCw size={14} className="me-1" /> Refresh
          </button>
          <Link to="/merchant/products/new" className="btn btn-sm btn-danger">
            <FiPlus size={14} className="me-1" /> Add Product
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-2">
        <div className="col-6 col-md-3">
          <KpiCard icon={FiDollarSign} label="Revenue (30d)" value={fmtShort(ov?.current?.revenue)} growth={ov?.growth?.revenue} color="#B91C1C" />
        </div>
        <div className="col-6 col-md-3">
          <KpiCard icon={FiShoppingBag} label="Orders (30d)" value={ov?.current?.orders || 0} growth={ov?.growth?.orders} color="#2563EB" />
        </div>
        <div className="col-6 col-md-3">
          <KpiCard icon={FiBarChart2} label="Avg Order Value" value={fmt(ov?.current?.avgOrderValue)} growth={ov?.growth?.avgOrderValue} color="#059669" />
        </div>
        <div className="col-6 col-md-3">
          <KpiCard icon={FiStar} label="Avg Rating" value={ov?.allTime?.avgRating || '—'} color="#EAB308" />
        </div>
      </div>
      <div className="row g-3 mb-2">
        <div className="col-6 col-md-2"><KpiCard icon={FiPackage} label="All-Time Orders" value={ov?.allTime?.orders || 0} color="#6B7280" /></div>
        <div className="col-6 col-md-2"><KpiCard icon={FiDollarSign} label="Lifetime Revenue" value={fmtShort(ov?.allTime?.revenue)} color="#059669" /></div>
        <div className="col-6 col-md-2"><KpiCard icon={FiBox} label="Active Products" value={ov?.allTime?.activeProducts || 0} color="#7C3AED" /></div>
        <div className="col-6 col-md-2"><KpiCard icon={FiClock} label="Pending Orders" value={ov?.allTime?.pendingOrders || 0} color="#F59E0B" /></div>
        <div className="col-6 col-md-2"><KpiCard icon={FiUsers} label="Total Reviews" value={ov?.allTime?.reviews || 0} color="#0891B2" /></div>
        <div className="col-6 col-md-2"><KpiCard icon={FiUsers} label="Repeat Rate" value={`${customers?.summary?.repeatRate || 0}%`} color="#E11D48" /></div>
      </div>

      {/* Revenue Chart */}
      <SectionTitle icon={FiTrendingUp} title="Revenue Trend" action={
        <div className="btn-group btn-group-sm">
          {[30, 60, 90].map(d => (
            <button key={d} className={`btn ${revDays === d ? 'btn-danger' : 'btn-outline-secondary'}`} onClick={() => setRevDays(d)}>{d}d</button>
          ))}
        </div>
      } />
      <div className="dashboard-card p-3 mb-3">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={revenue} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#B91C1C" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#B91C1C" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickFormatter={v => v?.slice(5)} />
            <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} tickFormatter={v => fmtShort(v)} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#B91C1C" fill="url(#revGrad)" strokeWidth={2} />
            <Area type="monotone" dataKey="orders" name="Orders" stroke="#EAB308" fill="none" strokeWidth={1.5} strokeDasharray="4 4" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="row g-3">
        {/* Order Status Pie */}
        <div className="col-md-6">
          <SectionTitle icon={FiPieChart} title="Orders by Status" />
          <div className="dashboard-card p-3">
            {orders?.byStatus?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={orders.byStatus} dataKey="count" nameKey="status" cx="50%" cy="50%"
                    outerRadius={90} innerRadius={50} paddingAngle={2}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {orders.byStatus.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-center text-muted py-5"><FiPieChart size={32} className="mb-2 d-block mx-auto" /><small>No order data</small></div>}
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="col-md-6">
          <SectionTitle icon={FiBarChart2} title="Conversion Funnel" />
          <div className="dashboard-card p-3">
            {funnel ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={[
                    { stage: 'Views', value: funnel.productViews },
                    { stage: 'Orders', value: funnel.orders },
                    { stage: 'Delivered', value: funnel.delivered },
                    { stage: 'Cancelled', value: funnel.cancelled },
                  ]} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                    <Tooltip />
                    <Bar dataKey="value" name="Count" radius={[6, 6, 0, 0]}>
                      {[0, 1, 2, 3].map(i => <Cell key={i} fill={['#D1D5DB', '#3B82F6', '#10B981', '#EF4444'][i]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="d-flex justify-content-around mt-2 text-center">
                  <div><FiCheckCircle color="#10B981" size={14} /><div style={{ fontSize: '18px', fontWeight: 800, color: '#10B981' }}>{funnel.completionRate}%</div><small className="text-muted">Completion</small></div>
                  <div><FiXCircle color="#EF4444" size={14} /><div style={{ fontSize: '18px', fontWeight: 800, color: '#EF4444' }}>{funnel.cancellationRate}%</div><small className="text-muted">Cancellation</small></div>
                </div>
              </>
            ) : <div className="text-center text-muted py-5"><FiBarChart2 size={32} className="mb-2 d-block mx-auto" /><small>No data</small></div>}
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Category Breakdown */}
        <div className="col-md-6">
          <SectionTitle icon={FiPieChart} title="Products by Category" />
          <div className="dashboard-card p-3">
            {products?.byCategory?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={products.byCategory.map(c => ({ name: c._id || 'Uncategorized', value: c.count }))} dataKey="value" cx="50%" cy="50%" outerRadius={90}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {products.byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="text-center text-muted py-5"><small>No products</small></div>}
          </div>
        </div>

        {/* Review Distribution */}
        <div className="col-md-6">
          <SectionTitle icon={FiStar} title={`Review Distribution (${reviews?.total || 0})`} />
          <div className="dashboard-card p-3">
            {reviews?.distribution?.length > 0 ? (
              <div>
                {reviews.distribution.slice().reverse().map(d => (
                  <div key={d.stars} className="d-flex align-items-center gap-2 mb-2">
                    <span style={{ fontSize: '13px', fontWeight: 700, width: '24px' }}>{d.stars}★</span>
                    <div style={{ flex: 1, height: '18px', background: '#F3F4F6', borderRadius: '9px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${d.percentage}%`, height: '100%', borderRadius: '9px',
                        background: d.stars >= 4 ? '#10B981' : d.stars === 3 ? '#F59E0B' : '#EF4444',
                        transition: 'width 0.6s',
                      }} />
                    </div>
                    <span style={{ fontSize: '12px', color: '#6B7280', width: '55px', textAlign: 'right' }}>{d.count} ({d.percentage}%)</span>
                  </div>
                ))}
              </div>
            ) : <div className="text-center text-muted py-5"><FiStar size={32} className="mb-2 d-block mx-auto" /><small>No reviews</small></div>}
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Top Products */}
        <div className="col-md-6">
          <SectionTitle icon={FiTrendingUp} title="Top Products" />
          <div className="dashboard-card">
            {products?.topSelling?.length > 0 ? (
              <div className="list-group list-group-flush">
                {products.topSelling.slice(0, 5).map((p, i) => (
                  <div key={p._id} className="list-group-item d-flex align-items-center gap-3 py-3">
                    <span style={{ fontSize: '14px', fontWeight: 800, color: '#B91C1C', width: '20px' }}>#{i + 1}</span>
                    <img src={p.images?.[0]?.url || 'https://placehold.co/40x40/F3F4F6/9CA3AF?text=?'} alt=""
                      style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>{p.category} · ⭐ {p.ratings?.average || 0} ({p.ratings?.count || 0})</div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{fmt(p.price)}</div>
                  </div>
                ))}
              </div>
            ) : <div className="text-center text-muted py-4"><small>No products yet</small></div>}
          </div>
        </div>

        {/* Top Customers */}
        <div className="col-md-6">
          <SectionTitle icon={FiUsers} title="Top Customers" />
          <div className="dashboard-card">
            {customers?.topCustomers?.length > 0 ? (
              <div className="list-group list-group-flush">
                {customers.topCustomers.slice(0, 5).map((c, i) => (
                  <div key={i} className="list-group-item d-flex align-items-center gap-3 py-3">
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      background: COLORS[i % COLORS.length] + '20', color: COLORS[i % COLORS.length],
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: '13px',
                    }}>{(c.name || '?')[0]}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{c.name || 'Customer'}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>{c.orderCount} orders · Last: {new Date(c.lastOrder).toLocaleDateString()}</div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#059669' }}>{fmt(c.totalSpent)}</div>
                  </div>
                ))}
              </div>
            ) : <div className="text-center text-muted py-4"><small>No customers yet</small></div>}
          </div>
        </div>
      </div>

      {/* Low Stock Alerts */}
      {products?.lowStock?.length > 0 && (
        <>
          <SectionTitle icon={FiAlertTriangle} title={`Low Stock Alerts (${products.lowStock.length})`} />
          <div className="dashboard-card">
            <div className="list-group list-group-flush">
              {products.lowStock.map(p => (
                <Link key={p._id} to={`/merchant/products/${p._id}/edit`}
                  className="list-group-item list-group-item-action d-flex align-items-center gap-3 py-2"
                  style={{ borderLeft: p.stock?.quantity === 0 ? '3px solid #EF4444' : '3px solid #F59E0B' }}>
                  <img src={p.images?.[0]?.url || 'https://placehold.co/32x32/F3F4F6/9CA3AF?text=?'} alt=""
                    style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} />
                  <div style={{ flex: 1 }}><span style={{ fontSize: '13px', fontWeight: 500 }}>{p.name}</span></div>
                  <span className={`badge bg-${p.stock?.quantity === 0 ? 'danger' : 'warning'}`}>
                    {p.stock?.quantity === 0 ? 'OUT OF STOCK' : `${p.stock?.quantity} left`}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Recent Reviews */}
      {reviews?.recent?.length > 0 && (
        <>
          <SectionTitle icon={FiStar} title="Recent Reviews" />
          <div className="dashboard-card">
            <div className="list-group list-group-flush">
              {reviews.recent.slice(0, 5).map((r, i) => (
                <div key={i} className="list-group-item py-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <span style={{ fontSize: '13px', fontWeight: 600 }}>{r.userName || 'Customer'}</span>
                      <span className="text-muted ms-2" style={{ fontSize: '11px' }}>on {r.productName}</span>
                    </div>
                    <div>{Array.from({ length: 5 }).map((_, s) => (
                      <span key={s} style={{ color: s < r.rating ? '#EAB308' : '#E5E7EB', fontSize: '13px' }}>★</span>
                    ))}</div>
                  </div>
                  {r.comment && <p className="mb-0 mt-1" style={{ fontSize: '13px', color: '#4B5563' }}>{r.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MerchantDashboard;
